import * as React from "react";
import { ArrowLeft, ExternalLink, Plus } from "lucide-react";
import { Link, useParams } from "react-router";

import { useGetRenewalFamilyQuery } from "@/api/renewals";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { getUserDisplayName } from "@/helpers/userDisplay";
import { useDateFormatter } from "@/hooks/useDateFormatter";
import { ModifySnapshotEventDialog } from "@/modules/snapshots/snapshotEvents/modifySnapshotEventDialog";
import { formatGbp } from "@/views/spend/money-format";
import { SummaryText } from "@/views/suppliers/components/contract-family-enrichment";
import { cn } from "@/lib/utils";

import { LegalReviewSection } from "./components/legal-review-section";
import { ACTION_DATE_LABEL, statusLabel, statusVariant } from "./constants";
import { useUserDirectory } from "./use-owner-labels";

const LABEL_CLASS = "text-[11px] font-semibold uppercase tracking-wide text-gray-500";
const EMPTY = "\u2013";

interface FieldProps {
  label: string;
  value: React.ReactNode;
  markdown?: boolean;
}

// Long model text gets a card per field so adjacent fields never read as one block;
// short values are plain cells inside a ValueGroup.
const Field = ({ label, value, markdown = false }: FieldProps) => (
  <div className={markdown ? "rounded-md border border-[#e4ddd5] bg-white px-4 py-3" : undefined}>
    <dt className={LABEL_CLASS}>{label}</dt>
    <dd className={cn("mt-1 text-sm text-[#111b35]", !markdown && "whitespace-pre-wrap")}>
      {markdown && typeof value === "string" ? <SummaryText text={value} /> : (value ?? EMPTY)}
    </dd>
  </div>
);

interface SectionProps {
  title: string;
  children: React.ReactNode;
}

const Section = ({ title, children }: SectionProps) => (
  <section className="rounded-lg border border-[#e4ddd5] bg-[#fffdfb] p-5" aria-label={title}>
    <h2 className="text-sm font-bold uppercase tracking-wide text-[#111b35]">{title}</h2>
    <div className="mt-4 space-y-2">{children}</div>
  </section>
);

const ValueGroup = ({ children }: { children: React.ReactNode }) => (
  <dl className="grid gap-4 rounded-md border border-[#e4ddd5] bg-[#f7f3ee]/50 p-4 md:grid-cols-3">{children}</dl>
);

const FieldCards = ({ children }: { children: React.ReactNode }) => <dl className="space-y-2">{children}</dl>;

export const RenewalDetailPage = () => {
  const { relationId = "" } = useParams();
  const { formatDate } = useDateFormatter();
  const { data: family, isLoading, isError } = useGetRenewalFamilyQuery(relationId, { skip: !relationId });
  // One directory lookup for the owner, the legal representative and the note authors.
  const directory = useUserDirectory(
    React.useMemo(
      () => [
        family?.owner_user_id ?? null,
        family?.legal_review.representative_user_id ?? null,
        ...(family?.legal_notes.map(note => note.author_user_id) ?? []),
      ],
      [family?.owner_user_id, family?.legal_review.representative_user_id, family?.legal_notes],
    ),
  );
  const [eventOpen, setEventOpen] = React.useState(false);

  const date = (value: string | null) => (value ? formatDate(value) : EMPTY);
  const owner = family?.owner_user_id ? directory.get(family.owner_user_id) : undefined;
  const termRemaining = family?.days_to_term_end ?? null;

  return (
    <main
      className="-mx-4 -my-8 min-h-screen bg-[#f7f3ee] px-5 py-6 sm:-mx-6 lg:-mx-8 lg:px-8 lg:py-7"
      aria-label="Renewal detail"
    >
      <div className="mx-auto flex max-w-5xl flex-col gap-4">
        <Link to="/renewal-centre" className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-[#111b35]">
          <ArrowLeft className="size-4" aria-hidden="true" /> Renewal Centre
        </Link>
        {isLoading ? <p className="text-sm text-gray-500">Loading Contract Family…</p> : null}
        {isError ? (
          <p className="text-sm text-red-700" role="alert">
            This Contract Family is not available.
          </p>
        ) : null}
        {family ? (
          <>
            <header className="flex flex-col gap-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">{family.supplier_name}</p>
              <div className="flex flex-wrap items-center gap-3">
                <h1 className="text-2xl font-bold text-[#111b35]">{family.family_name}</h1>
                {family.procurement_status ? (
                  <Badge variant={statusVariant(family.procurement_status)} className="uppercase tracking-wide">
                    {statusLabel(family.procurement_status)}
                  </Badge>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                {family.category_l3 ? (
                  <Badge variant="outline" className="font-medium text-gray-600">
                    {family.category_l3}
                  </Badge>
                ) : null}
                {family.external_id ? <span>SPID: {family.external_id}</span> : null}
                {owner ? <span>Owner: {owner.email || getUserDisplayName(owner)}</span> : null}
              </div>
              <div className="flex flex-wrap justify-end gap-2">
                <Button asChild type="button" variant="outline">
                  <Link
                    to={`/drive/${encodeURIComponent(family.fs_entry_id)}?supplierContractFolderId=${encodeURIComponent(family.relation_id)}&supplierId=${encodeURIComponent(family.supplier_id)}`}
                  >
                    Documents
                  </Link>
                </Button>
                {family.contract_summary ? (
                  <Button asChild type="button" variant="outline">
                    <Link to={`/reports/outcome/${encodeURIComponent(family.contract_summary.outcome_id)}`}>
                      View contract summary <ExternalLink className="ml-1.5 size-3.5" aria-hidden="true" />
                    </Link>
                  </Button>
                ) : null}
                {family.contract_summary ? (
                  <Button type="button" onClick={() => setEventOpen(true)}>
                    <Plus className="mr-1.5 size-4" aria-hidden="true" /> Create event
                  </Button>
                ) : null}
              </div>
            </header>

            <section
              className="grid gap-4 rounded-lg border border-[#e4ddd5] bg-[#fffdfb] p-5 md:grid-cols-3"
              aria-label="Key renewal figures"
            >
              <div>
                <p className={LABEL_CLASS}>Term end date</p>
                <p className="mt-1 text-xl font-bold text-[#111b35]">{date(family.term_end_date)}</p>
                {termRemaining !== null ? (
                  <p className="text-xs text-gray-500">
                    {termRemaining >= 0 ? `${termRemaining} days remaining` : `Ended ${-termRemaining} days ago`}
                  </p>
                ) : null}
              </div>
              <div>
                <p className={LABEL_CLASS}>Action date</p>
                <p className="mt-1 text-xl font-bold text-[#111b35]">{date(family.action_date)}</p>
                {family.notice_period_days !== null ? (
                  <p className="text-xs text-gray-500">{family.notice_period_days} days&apos; notice required</p>
                ) : null}
              </div>
              <div>
                <p className={LABEL_CLASS}>Committed value</p>
                <p className="mt-1 text-xl font-bold text-[#111b35]">
                  {formatGbp(family.total_contract_value_gbp) ?? "Not available"}
                </p>
                {family.currency_code ? (
                  <p className="text-xs text-gray-500">Local currency: {family.currency_code}</p>
                ) : null}
              </div>
            </section>

            <Section title="Contract">
              <FieldCards>
                <Field label="Contract title" value={family.contract_title} markdown />
                <Field label="Description of the contract" value={family.description} markdown />
              </FieldCards>
            </Section>

            <Section title="Term & renewal">
              <ValueGroup>
                <Field label="Commencement date" value={date(family.commencement_date)} />
                <Field
                  label="Initial term"
                  value={family.initial_term_months !== null ? `${family.initial_term_months} months` : null}
                />
                <Field label="Termination type" value={family.termination_type} />
              </ValueGroup>
              <FieldCards>
                <Field label="Term structure" value={family.term_structure} markdown />
                <Field label="Price escalation" value={family.price_escalation} markdown />
                <Field label="Break clauses / termination" value={family.break_clauses} markdown />
              </FieldCards>
            </Section>

            <Section title="Notice">
              <ValueGroup>
                <Field label="Notice period (days)" value={family.notice_period_days} />
              </ValueGroup>
              <FieldCards>
                <Field label="Notice service requirements" value={family.notice_service_requirements} markdown />
              </FieldCards>
            </Section>

            <Section title="Key dates">
              <ValueGroup>
                <Field label="Auto-renewal notice deadline" value={date(family.auto_renewal_notice_deadline)} />
                <Field label="Optional renewal deadline" value={date(family.optional_renewal_deadline)} />
                <Field label="Early termination deadline" value={date(family.early_termination_deadline)} />
              </ValueGroup>
            </Section>

            <LegalReviewSection
              key={family.relation_id}
              relationId={family.relation_id}
              review={family.legal_review}
              notes={family.legal_notes}
              canEdit={family.can_edit_legal}
              directory={directory}
            />

            {eventOpen && family.contract_summary ? (
              <ModifySnapshotEventDialog
                mode="create"
                open={eventOpen}
                onClose={() => setEventOpen(false)}
                businessReportOutcomeId={family.contract_summary.outcome_id}
                businessReportOutcomeResultId={family.contract_summary.action_date_result_id}
                eventType={ACTION_DATE_LABEL}
                eventDate={family.action_date ? new Date(family.action_date) : undefined}
              />
            ) : null}
          </>
        ) : null}
      </div>
    </main>
  );
};
